/**
 * Statistics block: animate figure when section scrolls into view (once).
 */
const mqReduce = window.matchMedia("(prefers-reduced-motion: reduce)");

function parseFigureEnd(raw) {
  const s = String(raw ?? "").trim();
  if (s === "") {
    return null;
  }
  const m = s.match(/^([0-9][0-9,]*(?:\.[0-9]+)?)\s*(.*)$/);
  if (!m) {
    return { numeric: false, display: s };
  }
  const n = parseFloat(m[1].replace(/,/g, ""));
  if (Number.isNaN(n)) {
    return { numeric: false, display: s };
  }
  return {
    numeric: true,
    end: n,
    suffix: m[2] ?? "",
  };
}

function formatFigureNumber(n, hasDecimal) {
  if (hasDecimal) {
    const rounded = Math.round(n * 10) / 10;
    return String(rounded);
  }
  return Math.round(n).toLocaleString("en-US");
}

function runFigureAnimation(el, parsed, durationMs) {
  const start = performance.now();
  const from = 0;
  const to = parsed.end;
  const suffix = parsed.suffix;
  const hasDecimal = !Number.isInteger(to);

  const tick = (now) => {
    const t = Math.min((now - start) / durationMs, 1);
    const eased = 1 - (1 - t) ** 2;
    const cur = from + (to - from) * eased;
    el.textContent = `${formatFigureNumber(cur, hasDecimal)}${suffix}`;
    if (t < 1) {
      requestAnimationFrame(tick);
    }
  };

  requestAnimationFrame(tick);
}

function primeFigures(section) {
  const reduced = mqReduce.matches;
  section.querySelectorAll("[data-stat-counter]").forEach((el) => {
    const raw = el.getAttribute("data-stat-end") ?? "";
    const parsed = parseFigureEnd(raw);
    if (!parsed) {
      el.textContent = "";
      return;
    }
    if (!parsed.numeric) {
      el.textContent = parsed.display;
      return;
    }
    if (reduced) {
      el.textContent = `${formatFigureNumber(parsed.end, !Number.isInteger(parsed.end))}${parsed.suffix}`;
      return;
    }
    el.textContent = `${formatFigureNumber(0, !Number.isInteger(parsed.end))}${parsed.suffix}`;
  });
}

function initStatisticsSection(section) {
  if (section.dataset.statisticsAnimated === "1") {
    return;
  }

  primeFigures(section);

  const counters = section.querySelectorAll("[data-stat-counter]");
  const hasNumeric = Array.from(counters).some((el) => {
    const p = parseFigureEnd(el.getAttribute("data-stat-end") ?? "");
    return p && p.numeric === true;
  });

  if (mqReduce.matches || !hasNumeric) {
    section.dataset.statisticsAnimated = "1";
    return;
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.target.dataset.statisticsAnimated === "1") {
          return;
        }
        entry.target.dataset.statisticsAnimated = "1";
        const durationMs = 1400;
        entry.target.querySelectorAll("[data-stat-counter]").forEach((el) => {
          const parsed = parseFigureEnd(el.getAttribute("data-stat-end") ?? "");
          if (!parsed || !parsed.numeric) {
            return;
          }
          runFigureAnimation(el, parsed, durationMs);
        });
        obs.unobserve(entry.target);
      });
    },
    { rootMargin: "0px 0px -8% 0px", threshold: 0.15 },
  );

  observer.observe(section);
}

function initStatistics(root = document) {
  root.querySelectorAll("[data-statistics]").forEach(initStatisticsSection);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initStatistics());
} else {
  initStatistics();
}
